import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { CommonModule, NgIf, NgFor } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Platform, ToastController, AlertController } from '@ionic/angular/standalone';
import { GoogleAnalyticsService } from 'src/app/providers/google-analytics.service';
import { Storage } from '@ionic/storage';
import { UserCollectionsServices } from '../../providers/user-collection-services';
import { GlobalDataService } from '../../providers/global-data.service';
import { BtnComponent } from '../btn/btn.component';
import { common } from '../../../environments/environment';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-v2-add-multiple-items',
  standalone: true,
  imports: [CommonModule, NgIf, NgFor, FormsModule, TranslatePipe, BtnComponent],
  templateUrl: './v2-add-multiple-items.component.html',
  styleUrls: ['./v2-add-multiple-items.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class V2AddMultipleItemsComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('overlayRoot', { read: ElementRef }) overlayRootEl?: ElementRef<HTMLDivElement>;

  @Input() isOpen = false;
  @Input() itemsList: any[] = [];

  @Output() closed = new EventEmitter<any>();

  /** ——— Overlay content stagger ——— */
  contentReveal = false;

  domOpen = false;
  isClosing = false;
  private closeAnimationFinishTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly OVERLAY_CLOSE_ANIM_MS = 400;

  /** ——— Bulk-add form state (formerly add-multiple-items-content) ——— */
  itemIdToAdd: string;
  itemIdsToAdd = '';
  invalidItems: string;
  countInvalidItems: number;
  itemsToAdd: any = [];
  totalItemsToAddNew = 0;
  totalItemsToAdd = 0;
  notValidated = true;
  saving = false;

  userCollectionItemsOriginalList: any;
  userCollectionItemsList: any;
  activeCollection: any;
  activeColectionId: number;
  agentId: string;
  itemFound = false;

  saveList: any = [];
  saveListString: string;
  formattedItemList: string;

  private activeCollectionSub?: Subscription;
  private agentIdSub?: Subscription;

  constructor(
    private readonly gaService: GoogleAnalyticsService,
    private readonly platform: Platform,
    public readonly alertCtrl: AlertController,
    private readonly toastCtrl: ToastController,
    private readonly gds: GlobalDataService,
    public readonly localStorage: Storage,
    private readonly userColectionsService: UserCollectionsServices,
    public readonly translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.activeCollection = [];
    this.activeCollectionSub = this.gds.getActiveCollection().subscribe((data) => {
      if (!data?.collection) return;
      this.activeCollection = data.collection;
      this.activeColectionId =
        this.activeCollection.user_collection_id_key ??
        this.activeCollection.user_collection_id;
    });
    this.agentIdSub = this.gds.getAgentId().subscribe((data) => {
      this.agentId = data;
    });

    void this.localStorage.get('activeCollection').then(
      (activeCol) => {
        if (activeCol == null) {
          return;
        }
        if (
          activeCol.resource !== undefined &&
          Array.isArray(activeCol.resource)
        ) {
          this.activeCollection = activeCol.resource[0];
        } else {
          this.activeCollection = activeCol;
        }
        if (this.activeCollection?.collection !== undefined) {
          this.activeCollection = this.activeCollection.collection;
        }
        if (this.activeCollection?.user_collection_id_key === undefined) {
          this.activeColectionId = this.activeCollection?.user_collection_id;
        } else {
          this.activeColectionId = this.activeCollection?.user_collection_id_key;
        }
        if (this.activeColectionId === undefined || this.activeColectionId == null) {
          if (this.isNumber(this.activeCollection)) {
            this.activeColectionId = this.activeCollection;
          } else {
            this.emitDismiss({});
          }
        }
      },
      (error) => console.log(error)
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']) {
      if (this.isOpen) {
        this.clearCloseAnimationFinishTimer();
        const wasClosing = this.isClosing;
        this.isClosing = false;
        this.domOpen = true;
        this.contentReveal = false;
        this.setBodyScrollLock(true);
        if (wasClosing) {
          setTimeout(() => this.resetOverlayEnterAnimationStyles(), 0);
        }
        void this.trackView();
        this.hydrateFromItemsList();
        setTimeout(() => {
          if (!this.isOpen) return;
          this.contentReveal = true;
        }, 50);
      } else {
        this.contentReveal = false;
        if (this.domOpen) {
          this.isClosing = true;
          this.scheduleCloseAnimationFinish();
        }
      }
    }
    if (changes['itemsList'] && this.isOpen) {
      this.hydrateFromItemsList();
    }
  }

  ngOnDestroy(): void {
    this.clearCloseAnimationFinishTimer();
    this.setBodyScrollLock(false);
    this.activeCollectionSub?.unsubscribe();
    this.agentIdSub?.unsubscribe();
  }

  private hydrateFromItemsList(): void {
    if (this.itemsList == null) {
      return;
    }
    this.userCollectionItemsOriginalList = this.itemsList;
    this.userCollectionItemsList = JSON.parse(JSON.stringify(this.itemsList));
    this.notValidated = true;
    this.itemIdsToAdd = '';
    this.itemsToAdd = [];
    this.totalItemsToAddNew = 0;
    this.totalItemsToAdd = 0;
    this.saving = false;
    this.invalidItems = '';
    this.countInvalidItems = 0;
  }

  isNumber(n: any): boolean {
    return !isNaN(parseFloat(n)) && !isNaN(n - 0);
  }

  addItems(): void {
    this.invalidItems = '';
    this.countInvalidItems = 0;
    this.itemIdsToAdd = this.itemIdsToAdd.toUpperCase();
    const items = this.itemIdsToAdd.split(/,/);
    for (const item of items) {
      if (item !== '') {
        this.addItem(item.trim());
      }
    }
    this.itemIdsToAdd = '';
    if (this.countInvalidItems > 0) {
      void this.presentAlert();
      this.notValidated = true;
    } else {
      this.notValidated = false;
    }
  }

  addItem(itemId: string): void {
    this.itemFound = false;
    this.itemIdToAdd = itemId;

    if (this.itemIdToAdd !== '') {
      for (const item of this.userCollectionItemsList) {
        if (item.item_id == this.itemIdToAdd) {
          this.totalItemsToAdd = this.totalItemsToAdd + 1;
          this.itemFound = true;

          if (item.addQuantity) {
            item.addQuantity = item.addQuantity + 1;
          } else {
            item.addQuantity = 1;
            if (!item.Tenho) {
              this.totalItemsToAddNew = this.totalItemsToAddNew + 1;
            }
          }
        }
      }

      if (!this.itemFound) {
        this.countInvalidItems = this.countInvalidItems + 1;
        if (this.countInvalidItems > 1) {
          this.invalidItems = this.invalidItems + ',' + itemId;
        } else {
          this.invalidItems = this.invalidItems + ' ' + itemId;
        }
      }

      this.itemsToAdd = this.userCollectionItemsList.filter((row) => {
        const toAdd = row.addQuantity;
        return toAdd;
      });

      this.itemIdToAdd = '';
    }
  }

  async presentAlert(): Promise<void> {
    const alert = await this.alertCtrl.create({
      header: this.translate.instant('ITEMS_NOT_EXIST_ALERT'),
      subHeader:
        this.translate.instant('ITEMS_NOT_EXIST_ALERT_TEXT') + this.invalidItems,
      buttons: ['Ok'],
    });
    await alert.present();
  }

  async presentToast(message: string): Promise<void> {
    const toast = await this.toastCtrl.create({
      message,
      duration: 3000,
      position: 'bottom',
    });
    await toast.present();
  }

  removeItem(item: any): void {
    item.addQuantity = item.addQuantity - 1;

    this.totalItemsToAdd = this.totalItemsToAdd - 1;
    if (item.addQuantity == 0 && !item.Tenho) {
      this.totalItemsToAddNew = this.totalItemsToAddNew - 1;
    }

    this.itemsToAdd = this.userCollectionItemsList.filter((row) => {
      const toAdd = row.addQuantity;
      return toAdd;
    });
  }

  async saveItems(): Promise<void> {
    if (this.saving == true) return;
    this.saving = true;

    if (this.itemIdsToAdd != '') {
      const alert = await this.alertCtrl.create({
        header: this.translate.instant('PLEASE_ADD_PENDING_ITENS'),
        subHeader: this.translate.instant('PLEASE_ADD_PENDING_ITENS_TEXT'),
        buttons: ['Ok'],
      });
      await alert.present();
      this.saving = false;
      return;
    }
    this.formattedItemList = '';
    this.saveList = [];
    for (const item of this.itemsToAdd) {
      if (item.addQuantity > 0) {
        for (let q = 1; q <= item.addQuantity; q++) {
          this.saveList.push(item.item_id);
          this.saveListString = JSON.stringify(this.saveList);
          this.formattedItemList = this.saveListString.substr(1).slice(0, -1);
          this.formattedItemList = this.formattedItemList.replace(/\"/g, '');
        }
      }
    }

    await this.callAddItemsProcedure(this.activeColectionId, this.formattedItemList);
  }

  listChanged(): void {
    this.notValidated = true;
  }

  private async callAddItemsProcedure(collectionId: number, itemsList: string): Promise<void> {
    try {
      await this.userColectionsService.callAddMultipleItems(
        String(collectionId),
        itemsList,
        this.agentId.toString()
      );
      await this.presentToast(this.translate.instant('TOAST_ITEMS_ADDED'));
      this.saving = false;
      this.gds.publishRecalculateTradeOptions(true);
      void this.gaService.trackEvent('add_multiple_items_page', 'add_itens', 'success', 1);
      this.emitDismiss({ saved: true });
    } catch (err) {
      this.saving = false;
      void this.gaService.trackEvent('add_multiple_items_page', 'add_itens', 'success', 0);
      await this.presentToast(this.translate.instant('TOAST_ERROR_ADDING_ITEMS'));
    }
  }

  private async trackView(): Promise<void> {
    await this.gaService.trackPageView('add_multiple_items_page');
  }

  onHeaderBack(): void {
    this.emitDismiss({});
  }

  onSave(): void {
    if (this.notValidated || this.saving) return;
    void this.saveItems();
  }

  private emitDismiss(data: any): void {
    this.closed.emit(data);
  }

  private getOverlayCloseAnimDurationMs(): number {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return V2AddMultipleItemsComponent.OVERLAY_CLOSE_ANIM_MS;
    }
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 0
      : V2AddMultipleItemsComponent.OVERLAY_CLOSE_ANIM_MS;
  }

  private clearCloseAnimationFinishTimer(): void {
    if (this.closeAnimationFinishTimer) {
      clearTimeout(this.closeAnimationFinishTimer);
      this.closeAnimationFinishTimer = null;
    }
  }

  private scheduleCloseAnimationFinish(): void {
    this.clearCloseAnimationFinishTimer();
    const ms = this.getOverlayCloseAnimDurationMs();
    this.closeAnimationFinishTimer = setTimeout(() => {
      this.closeAnimationFinishTimer = null;
      this.isClosing = false;
      this.domOpen = false;
      this.setBodyScrollLock(false);
    }, ms);
  }

  private resetOverlayEnterAnimationStyles(): void {
    const el = this.overlayRootEl?.nativeElement;
    if (!el) return;
    el.style.animation = 'none';
    el.style.opacity = '1';
    el.style.transform = 'scale(1)';
    void el.offsetWidth;
    el.style.removeProperty('animation');
    el.style.removeProperty('opacity');
    el.style.removeProperty('transform');
  }

  private setBodyScrollLock(locked: boolean): void {
    if (typeof document === 'undefined') return;
    document.body.classList.toggle('v2-add-multiple-items-open', locked);
  }
}
